import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check, ChevronRight, Clock3, Database, FileAudio, FileText, Folder, ImagePlus, Link2, Music, RefreshCw, Settings2, Trash2, Tv, Upload } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useSession } from '../app/session';
import { ask } from '../components/Confirm';
import { useAssetUrls } from '../components/EntityForm';
import { ErrorBox, Loading, PageHeader, Toggle } from '../components/ui';
import { AUDIO_ACCEPT, MAX_AUDIO, assetFor, audioInfo, audioSeconds, fileSize, mmss, trackBody, type Asset, type Track } from './AodPage';
import { useCategories } from './MediaLibraryPage';

const EMPTY: Track = { id: '', title: '', artist: null, album: null, durationSec: null, coverAssetId: null, sourceUrl: '', enabled: true, categories: [] };

export function AodEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const list = useQuery({ queryKey: ['aod', 'items'], queryFn: () => api.get<{ items: Track[] }>('/aod/tracks'), enabled: id !== 'new' });
  if (!id || id === 'new') return <Editor initial={null} />;
  if (list.isLoading) return <Loading />;
  const t = list.data?.items.find((x) => x.id === id);
  if (!t)
    return (
      <div className="card empty">
        This track was not found.{' '}
        <button className="btn small" onClick={() => navigate('/content/aod')}>
          Back to the library
        </button>
      </div>
    );
  return <Editor key={t.id} initial={t} />;
}

function Card({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <div className="card">
      <div className="row" style={{ gap: 10 }}>
        <span className="pkg-head-icon sm">{icon}</span>
        <strong style={{ fontSize: 16 }}>{title}</strong>
      </div>
      <div className="fe-body">{children}</div>
    </div>
  );
}

function Editor({ initial }: { initial: Track | null }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const url = useAssetUrls();
  const cats = useCategories('aod');
  const assets = useQuery({ queryKey: ['assets'], queryFn: () => api.get<{ items: Asset[] }>('/assets') });
  const [t, setT] = useState<Track>(initial ?? EMPTY);
  const [cover, setCover] = useState<string | null | undefined>(undefined);
  const [mode, setMode] = useState<'upload' | 'url'>('upload');
  const [dirty, setDirty] = useState(false);
  const [job, setJob] = useState<{ name: string; bytes: number; p: number; abort: () => void } | null>(null);
  const [upErr, setUpErr] = useState<unknown>(null);
  const [lastFile, setLastFile] = useState<{ name: string; bytes: number } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const coverInput = useRef<HTMLInputElement>(null);
  const set = (patch: Partial<Track>) => {
    setT((v) => ({ ...v, ...patch }));
    setDirty(true);
  };
  useEffect(() => {
    if (!dirty && !job) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, job]);
  const asset = assetFor(assets.data?.items ?? [], t.sourceUrl);
  useEffect(() => {
    if (initial && !asset && initial.sourceUrl && assets.data) setMode('url');
  }, [assets.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadAudio = async (file: File) => {
    setUpErr(null);
    if (file.size > MAX_AUDIO) return setUpErr(new Error(`The file is ${fileSize(file.size)}; the limit is 200 MB.`));
    const secs = await audioSeconds(file);
    const up = api.uploadWithProgress<{ url: string }>('/assets/audio', file, (p) => setJob((j) => (j ? { ...j, p } : j)));
    setJob({ name: file.name, bytes: file.size, p: 0, abort: up.abort });
    try {
      const a = await up.done;
      await qc.invalidateQueries({ queryKey: ['assets'] });
      const title = t.title || file.name.replace(/\.[^.]+$/, '').replace(/_+/g, ' ').trim();
      set({ sourceUrl: new URL(a.url, location.origin).href, title, ...(secs ? { durationSec: secs } : {}) });
      setLastFile({ name: file.name, bytes: file.size });
    } catch (e) {
      setUpErr(e);
    } finally {
      setJob(null);
    }
  };
  const coverUpload = useMutation({
    mutationFn: (f: File) => api.upload<{ id: string; url: string }>('/assets', f),
    onSuccess: async (a) => {
      await qc.invalidateQueries({ queryKey: ['assets'] });
      set({ coverAssetId: a.id });
      setCover(a.url);
    },
  });
  const done = async () => {
    await qc.invalidateQueries({ queryKey: ['aod', 'items'] });
    setDirty(false);
    navigate('/content/aod');
  };
  const save = useMutation({
    mutationFn: () => {
      const body = trackBody({ ...t, title: t.title.trim(), sourceUrl: t.sourceUrl.trim(), artist: t.artist?.trim() || null, album: t.album?.trim() || null });
      return initial ? api.put(`/aod/tracks/${initial.id}`, body) : api.post('/aod/tracks', body);
    },
    onSuccess: done,
  });
  const remove = useMutation({ mutationFn: () => api.delete(`/aod/tracks/${initial!.id}`), onSuccess: done });
  const leave = () => (dirty ? ask('Leave without saving? Your changes to this track will be lost.', () => navigate('/content/aod'), { action: 'Leave', danger: true }) : navigate('/content/aod'));
  const editable = can('ADMIN');
  const coverUrl = cover !== undefined ? cover : url(t.coverAssetId);
  const info = audioInfo(t.sourceUrl, asset);
  const urlOk = /^https?:\/\/\S+$/.test(t.sourceUrl.trim());
  const catList = cats.data?.items ?? [];
  const catIds = t.categories.map((c) => c.categoryId);
  const row = (icon: ReactNode, label: string, value: ReactNode) => (
    <div className="fac-detail-row">
      {icon}
      <span>{label}</span>
      <span className="fac-detail-val">{value}</span>
    </div>
  );

  return (
    <>
      <div className="crumbs">
        <button type="button" onClick={leave}>
          AOD
        </button>
        <ChevronRight size={14} />
        <button type="button" onClick={leave}>
          Music
        </button>
        <ChevronRight size={14} />
        <strong>{initial ? 'Edit track' : 'Add track'}</strong>
      </div>
      <PageHeader
        title={initial ? `Edit track · ${initial.title}` : 'Add track'}
        sub="The details, audio file, cover and TV visibility of this track."
        actions={
          <>
            <button className="btn" onClick={leave}>
              <ArrowLeft size={16} /> Back to library
            </button>
            {editable && (
              <button className="btn primary" disabled={!t.title.trim() || !urlOk || save.isPending || !!job} onClick={() => save.mutate()}>
                <Check size={16} /> {save.isPending ? 'Saving…' : initial ? 'Save changes' : 'Add track'}
              </button>
            )}
          </>
        }
      />
      <ErrorBox error={save.error ?? remove.error} />
      <div className="stat-strip">
        <div className="stat-tile">
          <span className={`stat-tile-icon ${t.enabled ? 'green' : 'grey'}`}>
            <Tv size={22} />
          </span>
          <span style={{ flex: 1 }}>
            <span className="stat-tile-label">Visible on TV</span>
            <span className="stat-tile-value" style={{ fontSize: 20 }}>
              {t.enabled ? 'Yes' : 'No'}
            </span>
            <span className="stat-tile-sub">{t.enabled ? 'Available for guests in rooms' : 'Hidden from the TVs'}</span>
          </span>
          <Toggle on={t.enabled} onChange={(v) => editable && set({ enabled: v })} label="Visible on TV" />
        </div>
        <div className="stat-tile">
          <span className="stat-tile-icon violet">
            <Database size={22} />
          </span>
          <span>
            <span className="stat-tile-label">Media source</span>
            <span className="stat-tile-value" style={{ fontSize: 20 }}>
              {t.sourceUrl ? info.source.replace(/ \(.*/, '') : 'Not set'}
            </span>
            <span className="stat-tile-sub">{asset ? 'Stored on this server' : t.sourceUrl ? 'Played from its URL' : 'Upload a file or enter a URL'}</span>
          </span>
        </div>
        <div className="stat-tile">
          <span className="stat-tile-icon blue">
            <Folder size={22} />
          </span>
          <span>
            <span className="stat-tile-label">Categories</span>
            <span className="stat-tile-value" style={{ fontSize: 20 }}>
              {catIds.length}
            </span>
            <span className="stat-tile-sub">{catIds.length ? 'Packages with these play it' : 'No package plays it yet'}</span>
          </span>
        </div>
      </div>

      <div className="vod-edit">
        <div className="stack">
          <Card icon={<FileText size={18} />} title="Basic information">
            <div className="camp-grid" style={{ marginTop: 0 }}>
              <label className="camp-field">
                <span className="camp-label">
                  Title <span style={{ color: '#dc2626' }}>*</span>
                </span>
                <input value={t.title} maxLength={200} disabled={!editable} onChange={(e) => set({ title: e.target.value })} autoFocus={!initial} />
              </label>
              <label className="camp-field">
                <span className="camp-label">Duration (seconds)</span>
                <span className="row" style={{ gap: 10, flexWrap: 'nowrap' }}>
                  <input
                    type="number"
                    min={1}
                    value={t.durationSec ?? ''}
                    disabled={!editable}
                    onChange={(e) => set({ durationSec: e.target.value ? Math.max(1, Math.round(Number(e.target.value))) || null : null })}
                    style={{ flex: 1 }}
                  />
                  <span className="muted" style={{ whiteSpace: 'nowrap' }}>
                    {mmss(t.durationSec)}
                  </span>
                </span>
                <span className="camp-hint">Filled in from the file when you upload one.</span>
              </label>
              <label className="camp-field">
                <span className="camp-label">Artist</span>
                <input value={t.artist ?? ''} maxLength={200} disabled={!editable} onChange={(e) => set({ artist: e.target.value })} placeholder="Kevin MacLeod" />
              </label>
              <label className="camp-field">
                <span className="camp-label">Album / source</span>
                <input value={t.album ?? ''} maxLength={200} disabled={!editable} onChange={(e) => set({ album: e.target.value })} placeholder="incompetech.com · CC BY 4.0" />
              </label>
            </div>
            <div className="camp-field">
              <span className="camp-label">Categories</span>
              <span className="row" style={{ gap: 6 }}>
                {catList.map((c) => {
                  const on = catIds.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      className={`pick-chip${on ? ' on' : ''}`}
                      disabled={!editable}
                      onClick={() => set({ categories: on ? t.categories.filter((x) => x.categoryId !== c.id) : [...t.categories, { categoryId: c.id }] })}
                    >
                      {c.name}
                    </button>
                  );
                })}
                {catList.length === 0 && <span className="muted">Create categories first (Categories tab).</span>}
              </span>
            </div>
          </Card>

          <Card icon={<FileAudio size={18} />} title="Audio file & source">
            <div className="seg">
              <button type="button" className={mode === 'upload' ? 'on' : ''} onClick={() => setMode('upload')}>
                <Upload size={15} /> Upload file
              </button>
              <button type="button" className={mode === 'url' ? 'on' : ''} onClick={() => setMode('url')}>
                <Link2 size={15} /> External URL
              </button>
            </div>
            {mode === 'upload' ? (
              <div className="audio-up">
                <div
                  className="audio-drop"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const f = e.dataTransfer.files[0];
                    if (f && editable && !job) void uploadAudio(f);
                  }}
                >
                  {job ? (
                    <>
                      <strong>
                        Uploading {job.name} · {Math.floor(job.p * 100)}%
                      </strong>
                      <span className="upload-bar" style={{ width: '100%' }}>
                        <span style={{ width: `${job.p * 100}%` }} />
                      </span>
                      <button type="button" className="btn small" onClick={job.abort}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <Music size={30} />
                      <strong>Drag and drop an MP3 or AAC file here</strong>
                      <span>MP3, AAC or M4A, up to 200 MB</span>
                      {editable && (
                        <button type="button" className="btn primary small" onClick={() => fileInput.current?.click()}>
                          {t.sourceUrl ? (
                            <>
                              <RefreshCw size={14} /> Replace file
                            </>
                          ) : (
                            <>
                              <Upload size={14} /> Choose file
                            </>
                          )}
                        </button>
                      )}
                    </>
                  )}
                </div>
                <div className="file-details">
                  <strong>File details</strong>
                  {row(<FileAudio size={15} />, 'File name', lastFile?.name ?? info.file)}
                  {row(<FileText size={15} />, 'Format', t.sourceUrl ? info.format : '—')}
                  {row(<Database size={15} />, 'File size', lastFile ? fileSize(lastFile.bytes) : asset ? fileSize(asset.bytes) : '—')}
                  {row(<Clock3 size={15} />, 'Duration', mmss(t.durationSec))}
                </div>
              </div>
            ) : (
              <label className="camp-field">
                <span className="camp-label">
                  Audio URL (MP3 / AAC) <span style={{ color: '#dc2626' }}>*</span>
                </span>
                <input className="mono" value={t.sourceUrl} maxLength={2000} disabled={!editable} onChange={(e) => set({ sourceUrl: e.target.value })} placeholder="https://example.com/audio-file.mp3" />
                <span className={`camp-hint${t.sourceUrl && !urlOk ? ' err' : ''}`}>{t.sourceUrl && !urlOk ? 'Enter a full http:// or https:// address.' : 'The TVs play this address directly.'}</span>
              </label>
            )}
            <ErrorBox error={upErr} />
            <input
              ref={fileInput}
              type="file"
              accept={AUDIO_ACCEPT}
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) void uploadAudio(f);
              }}
            />
          </Card>

          <Card icon={<ImagePlus size={18} />} title="Artwork">
            <div className="art-picker">
              <button type="button" className="art-thumb wide" disabled={!editable || coverUpload.isPending} onClick={() => coverInput.current?.click()}>
                {coverUrl ? <img src={coverUrl} alt="" /> : <ImagePlus size={22} />}
              </button>
              <div>
                <div className="camp-label">Cover</div>
                <div className="row" style={{ gap: 8, marginTop: 8 }}>
                  <button type="button" className="btn btn-soft small" disabled={!editable || coverUpload.isPending} onClick={() => coverInput.current?.click()}>
                    <Upload size={14} /> {coverUpload.isPending ? 'Uploading…' : 'Choose cover'}
                  </button>
                  {coverUrl && editable && (
                    <button
                      type="button"
                      className="btn small"
                      onClick={() => {
                        set({ coverAssetId: null });
                        setCover(null);
                      }}
                    >
                      <Trash2 size={14} /> Remove
                    </button>
                  )}
                </div>
                <div className="camp-hint" style={{ marginTop: 8 }}>
                  Shown on the music player. JPG or PNG, e.g. 1200 × 675 or square.
                </div>
                <ErrorBox error={coverUpload.error} />
              </div>
              <input
                ref={coverInput}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) coverUpload.mutate(f);
                }}
              />
            </div>
          </Card>
        </div>

        <div className="stack vod-side">
          <div className="card">
            <div className="row" style={{ gap: 8, fontWeight: 700 }}>
              <Music size={18} color="var(--brand)" /> Track preview
            </div>
            <div className="track-cover" style={{ marginTop: 12 }}>
              {coverUrl ? <img src={coverUrl} alt="" /> : <Music size={40} />}
            </div>
            {urlOk && <audio key={t.sourceUrl} className="track-audio" controls preload="none" src={t.sourceUrl} />}
            <h3 style={{ margin: '12px 0 0' }}>{t.title || 'Untitled'}</h3>
            <div className="muted">{t.artist || 'Unknown artist'}</div>
            {catIds.length > 0 && (
              <div className="row" style={{ gap: 6, marginTop: 8 }}>
                {catIds.map((id) => (
                  <span key={id} className="chip" style={{ color: '#5b21b6', background: '#f1ebff' }}>
                    {catList.find((c) => c.id === id)?.name ?? '…'}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="card">
            <div className="row" style={{ gap: 8, fontWeight: 700 }}>
              <Settings2 size={18} color="var(--brand)" /> Technical details
            </div>
            <div className="fac-detail-list" style={{ borderTop: 0 }}>
              {row(<FileAudio size={16} />, 'Audio format', t.sourceUrl ? info.format : '—')}
              {row(<Clock3 size={16} />, 'Duration', t.durationSec ? `${mmss(t.durationSec)} (${t.durationSec} s)` : '—')}
              {row(<Database size={16} />, 'File size', asset ? fileSize(asset.bytes) : lastFile ? fileSize(lastFile.bytes) : '—')}
              {row(<Link2 size={16} />, 'Source type', t.sourceUrl ? info.source : '—')}
              {row(<Tv size={16} />, 'Visible on TV', t.enabled ? 'Yes' : 'No')}
            </div>
          </div>
          {initial && editable && (
            <div className="card danger-zone">
              <Trash2 size={20} />
              <span style={{ flex: 1 }}>
                <strong>Danger zone</strong>
                <span className="muted" style={{ display: 'block', fontSize: 12.5 }}>
                  Removes the track from the library and the TVs.
                </span>
              </span>
              <button className="btn danger-outline" onClick={() => ask(`Delete ${initial.title}? It disappears from the TVs.`, () => remove.mutate())}>
                Delete track
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
